import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, UserRoundPlus, X } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Card } from '@shared/ui/containers/Card';
import { Button } from '@shared/ui/primitives/Button';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { formatDateTime } from '@shared/lib/format';
import { cn } from '@shared/lib/cn';
import { toast } from '@shared/ui/feedback/Toast';
import {
  fetchMemberRequests,
  approveMemberRequest,
  rejectMemberRequest,
  type MemberRequest,
} from './api';

/** Human-friendly labels for the common submitted-detail keys. */
const FIELD_LABELS: Record<string, string> = {
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email',
  phone: 'Phone',
  mobile: 'Mobile',
  id_number: 'ID number',
  national_id: 'ID number',
  member_type_id: 'Member type',
  member_type: 'Member type',
  unit: 'Unit',
  address: 'Address',
};

function prettyValue(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Admin: pending member join requests — view submitted details, approve/reject. */
export function MemberRequests(): JSX.Element {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['member-requests', 'pending'],
    queryFn: () => fetchMemberRequests('pending'),
  });
  const requests = query.data ?? [];

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        onBack={() => navigate('/app')}
        backLabel="Back home"
        eyebrow="Admin"
        title="Member requests"
        subtitle="Pending requests to join the community"
      />

      <div className="flex flex-col gap-3 px-5 pt-5 pb-safe-or-10">
        {query.isLoading ? (
          <>
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </>
        ) : query.isError ? (
          <ErrorState onRetry={() => query.refetch()} />
        ) : requests.length === 0 ? (
          <EmptyState
            icon={<UserRoundPlus className="h-7 w-7" strokeWidth={1.5} />}
            title="No pending requests."
            description="New join requests will appear here for approval."
          />
        ) : (
          requests.map((req) => <RequestCard key={req.member_request_id} req={req} />)
        )}
      </div>
    </div>
  );
}

function RequestCard({ req }: { req: MemberRequest }): JSX.Element {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['member-requests'] });

  const approveM = useMutation({
    mutationFn: () => approveMemberRequest(req.member_request_id),
    onSuccess: invalidate,
    onError: (e) => toast.danger("Couldn't approve", e instanceof Error ? e.message : undefined),
  });
  const rejectM = useMutation({
    mutationFn: () => rejectMemberRequest(req.member_request_id),
    onSuccess: invalidate,
    onError: (e) => toast.danger("Couldn't reject", e instanceof Error ? e.message : undefined),
  });
  const busy = approveM.isPending || rejectM.isPending;

  const name = req.requester_display || req.email || 'Unknown applicant';
  const details = Object.entries(req.member_data ?? {}).filter(([, v]) => v != null && v !== '');

  return (
    <Card noGrain className="rounded-xl p-4">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-start justify-between gap-3 text-left">
        <span className="min-w-0">
          <p className="font-display text-headline leading-tight text-ink">{name}</p>
          {req.email ? <p className="truncate text-body-sm text-ink-secondary">{req.email}</p> : null}
          <p className="mt-1 text-caption text-ink-muted">Requested {formatDateTime(req.created_at) || '—'}</p>
        </span>
        <ChevronDown className={cn('h-5 w-5 shrink-0 text-ink-muted transition-transform', open && 'rotate-180')} strokeWidth={1.75} />
      </button>

      {open ? (
        <div className="mt-3 border-t border-[color:var(--hairline)] pt-3">
          {details.length > 0 ? (
            <dl className="flex flex-col gap-1.5">
              {details.map(([k, v]) => (
                <div key={k} className="flex gap-2 text-body-sm">
                  <dt className="w-28 shrink-0 text-ink-muted">{FIELD_LABELS[k] ?? k}</dt>
                  <dd className="min-w-0 break-words text-ink">{prettyValue(v)}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-body-sm text-ink-secondary">No additional details submitted.</p>
          )}
        </div>
      ) : null}

      <div className="mt-3 flex gap-2">
        <Button variant="secondary" className="flex-1" loading={rejectM.isPending} disabled={busy} onClick={() => rejectM.mutate()}>
          <X className="mr-1 h-4 w-4" strokeWidth={2} /> Reject
        </Button>
        <Button className="flex-1" loading={approveM.isPending} disabled={busy} onClick={() => approveM.mutate()}>
          <Check className="mr-1 h-4 w-4" strokeWidth={2} /> Approve
        </Button>
      </div>
    </Card>
  );
}
